// src-lit/components/product/kd-pantry-product-quantity.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
  EVENT_NAMES,
  type PantryProductQuantityChangedDetail,
} from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

/**
 * @element kd-pantry-product-quantity
 * @description パントリー子商品カード単位の数量 +/- を制御する。
 *   ボタン押下で内部 quantity を増減し、画面表示と alert の出し分けを行いつつ、
 *   親 <kd-pantry-products> に向けて bubble するカスタムイベントを発火する。
 *
 *   data-prevent-add="true" のときは「他カート商品との同時購入不可」状態のため
 *   ボタンを Liquid 側で disabled にして無効化する（このコンポーネントは何もしない）。
 *
 *   同一 variantId のインスタンスが複数同時に存在し得る（カード + 詳細モーダル）。
 *   document レベルで bubble する `kd-pantry-product-quantity-changed` を購読し、
 *   peer の数量変更を表示反映する（再 dispatch はしないため、集計の二重発火は起きない）。
 */
@customElement('kd-pantry-product-quantity')
export class KdPantryProductQuantity extends LitElement {
  createRenderRoot() {
    return this;
  }

  private chooseButton: HTMLButtonElement | null = null;

  private quantityRow: HTMLElement | null = null;

  private minusButton: HTMLButtonElement | null = null;

  private plusButton: HTMLButtonElement | null = null;

  private quantityInput: HTMLInputElement | null = null;

  private overstockAlert: HTMLElement | null = null;

  private maxQuantityAlert: HTMLElement | null = null;

  private quantity = 0;

  private boundOnChoose = this.onChoose.bind(this);

  private boundOnMinus = this.onMinus.bind(this);

  private boundOnPlus = this.onPlus.bind(this);

  private boundOnInputChange = this.onInputChange.bind(this);

  private boundOnPeerChanged = this.onPeerChanged.bind(this);

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.chooseButton = this.querySelector<HTMLButtonElement>(
      SELECTORS.PANTRY_PRODUCTS.CHOOSE_BUTTON,
    );
    this.quantityRow = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCTS.QUANTITY_ROW,
    );
    this.minusButton = this.querySelector<HTMLButtonElement>(
      SELECTORS.PANTRY_PRODUCTS.MINUS_BUTTON,
    );
    this.plusButton = this.querySelector<HTMLButtonElement>(
      SELECTORS.PANTRY_PRODUCTS.PLUS_BUTTON,
    );
    this.quantityInput = this.querySelector<HTMLInputElement>(
      SELECTORS.PANTRY_PRODUCTS.QUANTITY_DISPLAY,
    );
    this.overstockAlert = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCTS.OVERSTOCK_ALERT,
    );
    this.maxQuantityAlert = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCTS.MAX_QUANTITY_ALERT,
    );
    this.chooseButton?.addEventListener('click', this.boundOnChoose);
    this.minusButton?.addEventListener('click', this.boundOnMinus);
    this.plusButton?.addEventListener('click', this.boundOnPlus);
    this.quantityInput?.addEventListener('change', this.boundOnInputChange);

    // 後発インスタンス（モーダル内など）が同 variantId の先発インスタンスから
    // 現在量を引き継ぐ。dispatch はしない（先発が既に集計に通知済み）。
    this.adoptPeerQuantity();
    this.syncDisplay();

    document.addEventListener(
      EVENT_NAMES.PANTRY.QUANTITY_CHANGED,
      this.boundOnPeerChanged as EventListener,
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.chooseButton?.removeEventListener('click', this.boundOnChoose);
    this.minusButton?.removeEventListener('click', this.boundOnMinus);
    this.plusButton?.removeEventListener('click', this.boundOnPlus);
    this.quantityInput?.removeEventListener('change', this.boundOnInputChange);
    document.removeEventListener(
      EVENT_NAMES.PANTRY.QUANTITY_CHANGED,
      this.boundOnPeerChanged as EventListener,
    );
    this.chooseButton = null;
    this.quantityRow = null;
    this.minusButton = null;
    this.plusButton = null;
    this.quantityInput = null;
    this.overstockAlert = null;
    this.maxQuantityAlert = null;
  }

  /**
   * 同 variantId の他インスタンスから現在量を引き継ぐ。
   * モーダル内 instance が cloneNode 直後に connect されたときに、
   * カード側の最新 quantity を初期表示に取り込むために使う。
   */
  private adoptPeerQuantity(): void {
    const variantId = this.dataset.productVariantId;
    if (!variantId) return;
    const peer = Array.from(
      document.querySelectorAll<KdPantryProductQuantity>('kd-pantry-product-quantity'),
    ).find((p) => p !== this && p.dataset.productVariantId === variantId);
    if (peer) this.quantity = peer.quantity;
  }

  /**
   * 他インスタンスの数量変更を受けて自身の表示を同期する。
   * 自身が dispatch した event はスキップして無限ループを避ける。
   * 同期は表示のみ。集計（kd-pantry-products）は dispatch 元側が既に通知済み。
   */
  private onPeerChanged(event: CustomEvent<PantryProductQuantityChangedDetail>): void {
    if (event.target === this) return;
    const myId = this.dataset.productVariantId;
    if (!myId) return;
    if (event.detail.variantId !== myId) return;
    if (event.detail.quantity === this.quantity) return;
    this.quantity = event.detail.quantity;
    this.syncDisplay();
  }

  private getInventoryQuantity(): number {
    const v = Number(this.dataset.productInventoryQuantity);
    return Number.isFinite(v) ? v : Infinity;
  }

  private getMaxQuantity(): number {
    const raw = this.dataset.maxQuantity;
    if (!raw) return Infinity;
    const v = Number(raw);
    return Number.isFinite(v) && v > 0 ? v : Infinity;
  }

  private isPrevented(): boolean {
    return this.dataset.preventAdd === 'true';
  }

  /**
   * 数量を 0 に戻す（フッターの「クリア」から kd-pantry-products 経由で呼ばれる）。
   * 既に 0 なら何もしない（無駄な dispatch を避ける）。
   */
  reset(): void {
    if (this.quantity === 0) return;
    this.quantity = 0;
    this.syncDisplay();
    this.dispatchChange();
  }

  /**
   * 「えらぶ＋」押下。未選択（数量 0）から数量 1 にして +/- 行へ切替える。
   * prevent-add 時は数量を増やさず ATTENTION_REQUESTED を発火（onPlus と同じ UX）。
   */
  private onChoose(): void {
    if (this.isPrevented()) {
      this.dispatchEvent(
        new CustomEvent(EVENT_NAMES.PANTRY.ATTENTION_REQUESTED, {
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    if (this.quantity > 0) return;
    this.quantity = 1;
    this.syncDisplay();
    this.dispatchChange();
  }

  private onMinus(): void {
    if (this.quantity <= 0) return;
    this.quantity -= 1;
    this.syncDisplay();
    this.dispatchChange();
  }

  private onPlus(): void {
    if (this.isPrevented()) {
      // 同時購入不可状態: 数量は増やさず、ページ常駐アラートの再表示を要求する。
      // ボタンは aria-disabled で見た目だけ disabled にしているため click は届く。
      this.dispatchEvent(
        new CustomEvent(EVENT_NAMES.PANTRY.ATTENTION_REQUESTED, {
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    // 在庫数を 1 だけ超えた状態（quantity = inventory + 1）まで増やせる。
    // そこに到達したら overstock アラートと UI エラー状態を見せた上で disabled にする
    // （syncDisplay 側）。最大購入数 (maxQuantity) は素直にハードリミット。
    const max = Math.min(this.getInventoryQuantity() + 1, this.getMaxQuantity());
    if (this.quantity >= max) return;
    this.quantity += 1;
    this.syncDisplay();
    this.dispatchChange();
  }

  /**
   * 数量入力欄の手入力を反映する。
   * - 非数値/負値は現在量に戻す（NaN ガード）
   * - 在庫数 / 最大購入数を超える値もクランプせず受け入れる
   *   （超過時は overstock / maxQuantity アラートで通知）
   * - 同時購入不可状態で増加方向の入力は ATTENTION_REQUESTED を発火して現在量へ戻す
   *   （+ ボタンと同じ UX を入力経由でも踏襲する）
   */
  private onInputChange(): void {
    if (!this.quantityInput) return;
    const raw = this.quantityInput.value;
    const parsed = Math.floor(Number(raw));
    if (!Number.isFinite(parsed) || parsed < 0) {
      this.syncDisplay();
      return;
    }
    if (parsed > this.quantity && this.isPrevented()) {
      this.dispatchEvent(
        new CustomEvent(EVENT_NAMES.PANTRY.ATTENTION_REQUESTED, {
          bubbles: true,
          composed: true,
        }),
      );
      this.syncDisplay();
      return;
    }
    if (parsed === this.quantity) {
      // 値変化なし（不正→ floor 後同値など）でも、入力欄の見た目は revert する
      this.syncDisplay();
      return;
    }
    this.quantity = parsed;
    this.syncDisplay();
    this.dispatchChange();
  }

  /**
   * 数値表示・ボタン disabled・在庫超過アラートを内部状態に合わせて更新する。
   *
   * 在庫超過 / 最大購入数超過のアラートは本来 onPlus でブロックされる想定だが、
   * data-product-inventory-quantity が後から更新されたり初期表示に既に超過があるケースで
   * UI が破綻しないよう連動表示する。
   */
  private syncDisplay(): void {
    const inventory = this.getInventoryQuantity();
    const maxQuantity = this.getMaxQuantity();
    const isOverstock = this.quantity > inventory;
    const isMaxExceeded = Number.isFinite(maxQuantity) && this.quantity > maxQuantity;
    const isError = isOverstock || isMaxExceeded;

    if (this.quantityInput) {
      const value = String(this.quantity);
      // 編集中の focus 状態でも value を上書きすると IME が壊れるため、現値と異なる時のみ更新。
      if (this.quantityInput.value !== value) {
        this.quantityInput.value = value;
      }
    }
    if (this.minusButton) {
      this.minusButton.disabled = this.quantity <= 0;
    }
    if (this.plusButton) {
      // isPrevented() のときは aria-disabled で見た目だけ disabled にする（click は届く必要があるため）。
      // 在庫上限+1 / 最大購入数の上限ガードは disabled で本当に押せなくする。
      // 在庫上限+1 まで許容するのは、最後の +1 で overstock メッセージを見せて気づかせるため。
      const max = Math.min(inventory + 1, maxQuantity);
      this.plusButton.disabled = this.quantity >= max;
      this.plusButton.setAttribute('aria-disabled', this.isPrevented() ? 'true' : 'false');
    }
    if (this.overstockAlert) {
      this.overstockAlert.hidden = !isOverstock;
    }
    if (this.maxQuantityAlert) {
      this.maxQuantityAlert.hidden = !isMaxExceeded;
    }
    // 在庫超過 / 最大購入数超過時は +/- 行 & 数値表示を Figma 1122:44727 のエラー表示に切り替える
    // （bg-surface-card-sub + text-status-alert）。Liquid 側で data-pantry-quantity-state の
    // Tailwind variant を用意してあるので、ここでは属性切替のみ行う。
    const state = isError ? 'error' : 'normal';
    this.quantityRow?.setAttribute('data-pantry-quantity-state', state);
    this.quantityInput?.setAttribute('data-pantry-quantity-state', state);

    // 未選択（数量 0）は「えらぶ＋」ボタン、選択済み（1 以上）は +/- 行を表示する。
    const isSelected = this.quantity > 0;
    this.chooseButton?.setAttribute('data-choose-hidden', isSelected ? 'true' : 'false');
    this.quantityRow?.setAttribute('data-row-hidden', isSelected ? 'false' : 'true');
  }

  private dispatchChange(): void {
    const detail: PantryProductQuantityChangedDetail = {
      variantId: this.dataset.productVariantId ?? '',
      quantity: this.quantity,
      variantPrice: Number(this.dataset.productVariantPrice) || 0,
      weightG: Number(this.dataset.productWeightG) || 0,
      inventoryQuantity: this.getInventoryQuantity(),
      maxQuantity: this.getMaxQuantity(),
      title: this.dataset.productTitle ?? '',
      sku: this.dataset.productSku ?? '',
      expirationOn: this.dataset.expirationOn ?? '',
      volume: this.dataset.productVolume ?? '',
    };
    this.dispatchEvent(
      new CustomEvent<PantryProductQuantityChangedDetail>(
        EVENT_NAMES.PANTRY.QUANTITY_CHANGED,
        {
          bubbles: true,
          composed: true,
          detail,
        },
      ),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-product-quantity': KdPantryProductQuantity;
  }
}
